import { FC, useCallback, useEffect, useState } from 'react';

import {
    FinalCounterpartyType,
    RequisiteAgreementType,
    RequisiteRole,
    RequisitesFieldName,
} from 'utils/constants/requisites';

import { requisitesPopupElementShownRaw } from '@hh.ru/analytics-js-events/build/clickme/employer/header/profile/requisites_popup_element_shown';
import { ActionBar, BottomSheet, Button, Modal, NavigationBar, Text, useBreakpoint } from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';
import { fetchRequisites } from 'components/RequisitesFormModal/fetchRequisites';
import { RequisitesFormModalContent } from 'components/RequisitesFormModal/RequisitesFormModalContent/RequisitesFormModalContent';
import { saveRequisites } from 'components/RequisitesFormModal/saveRequisites';

import {
    resetRequisites,
    setAllRequisitesErrors,
    setRequisiteByName,
    setRequisitesFormOpen,
    setRequisitesPayers,
} from 'store/models/requisites';
import { AccountType } from 'store/models/user';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'RequisitesForm.title',
    buttons: {
        close: 'RequisitesForm.close',
        cancel: 'RequisitesForm.cancel',
        send: 'RequisitesForm.send',
    },
};

export const RequisitesFormModal: FC = () => {
    const { isRequisitesFormOpen: isOpen, formParams, requisites } = useSelector((state) => state.requisites);
    const accountType = useSelector((state) => state.user.user?.currentAccount.type);

    const [isLoading, setLoading] = useState(false);
    const [isRequisitesEditable, setRequisitesEditable] = useState(false);

    const trl = useTrls();
    const dispatch = useDispatch();

    const { isMobile } = useBreakpoint();

    const [isSaving, setSaving] = useState(false);

    const finalCounterpartyType = requisites.finalCounterpartyType;

    const saveRequisitesHandler = useCallback(() => {
        if (accountType === AccountType.Government) {
            dispatch([
                setRequisiteByName({
                    fieldName: RequisitesFieldName.IsAgency,
                    value:
                        finalCounterpartyType === FinalCounterpartyType.COZHH
                            ? RequisiteRole.Agency
                            : RequisiteRole.FinalAdvertiser,
                }),
                setRequisiteByName({
                    fieldName: RequisitesFieldName.IsAgreementDirect,
                    value: finalCounterpartyType === FinalCounterpartyType.COZHH ? RequisiteAgreementType.Direct : null,
                }),
            ]);
        }
        void dispatch(saveRequisites(setSaving, formParams?.callback));
    }, [dispatch, accountType, finalCounterpartyType, formParams]);

    useEffect(() => {
        if (isOpen) {
            requisitesPopupElementShownRaw();
            void dispatch(fetchRequisites(setLoading, setRequisitesEditable));
        }
    }, [isOpen, dispatch]);

    const onClose = () =>
        dispatch([
            resetRequisites(null),
            setRequisitesPayers([]),
            setAllRequisitesErrors({}),
            setRequisitesFormOpen(false),
        ]);

    const content = <RequisitesFormModalContent isRequisitesEditable={isRequisitesEditable} isLoading={isLoading} />;
    const actions = <CrossOutlinedSize24 onClick={onClose} initialColor="secondary" data-qa="close-modal-button" />;
    const footer = (
        <ActionBar
            primaryActions={
                !isRequisitesEditable ? (
                    <Button mode="secondary" style="accent" onClick={onClose} data-qa="requisites-form-modal-close">
                        {trl(TrlKeys.buttons.close)}
                    </Button>
                ) : (
                    <>
                        <Button
                            mode="secondary"
                            style="accent"
                            onClick={onClose}
                            data-qa="requisites-form-modal-cancel"
                        >
                            {trl(TrlKeys.buttons.cancel)}
                        </Button>
                        <Button
                            mode="primary"
                            style="accent"
                            onClick={saveRequisitesHandler}
                            disabled={isSaving}
                            data-qa="requisites-form-modal-submit"
                        >
                            {formParams?.buttonName || trl(TrlKeys.buttons.send)}
                        </Button>
                    </>
                )
            }
        />
    );

    return (
        <>
            {!isMobile ? (
                <Modal
                    visible={isOpen}
                    onClose={onClose}
                    size="large"
                    header={<MagritteModalHeader title={formParams?.title || trl(TrlKeys.title)} actions={actions} />}
                    footer={footer}
                >
                    {content}
                </Modal>
            ) : (
                <BottomSheet
                    visible={isOpen}
                    onClose={onClose}
                    header={
                        <NavigationBar
                            title={<Text typography="title-5-semibold">{formParams?.title || trl(TrlKeys.title)}</Text>}
                            right={actions}
                            showDivider="always"
                        />
                    }
                    footer={footer}
                >
                    {content}
                </BottomSheet>
            )}
        </>
    );
};
